import React, { useState } from "react";
import { STATE, LOCK_SVG, actionOf, panelOf } from "./model";
import { MoveRow, TEAM_ROW, MARK_ROW } from "./Panel";

const hhmm = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export function StateLine({ state, words, testid }) {
  if (!state || state === "fine") return null;
  const [, ic] = STATE[state] || STATE.fine;
  return (
    <div className="state" data-testid={testid}>
      <span className="ic">{ic === "lock" ? LOCK_SVG : ic}</span>
      {words ? <span className="st">{words}</span> : null}
    </div>
  );
}

export function Disc({ name }) {
  if (!name) return null;
  return <span className="owner"><span className="avatar">{name[0]}</span>{name}</span>;
}

// the press on record greys the card: state becomes "pressed" with who · time
export function effState(c, press) {
  if (press && c.state !== "closed") return ["pressed", c.state === "pressed" && c.state_words ? c.state_words : `Pressed · ${press.by || ""}${press.at ? " · " + hhmm(press.at) : ""}`];
  return [c.state || "fine", c.state_words];
}

function GroupBar({ g }) {
  if (!g || !g.total) return null;
  const pct = Math.round((100 * (g.done || 0)) / g.total);
  return (
    <>
      <div className="gline"><div className="prog"><i style={{ width: `${pct}%` }} /></div><span>{g.done} of {g.total} closed · {g.total - (g.done || 0)} open</span></div>
      {g.blocking_id && <div className="blocks">Holds up the rest: <b>{g.blocking_id} blocks</b></div>}
    </>
  );
}

const longDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
};
const stop = (e) => e.stopPropagation();
const OLD_MOVES = [...TEAM_ROW, ...MARK_ROW];

function MoreMenu({ c, ctx }) {
  const [open, setOpen] = useState(false);
  const opts = c.options || [];
  const seen = new Set(OLD_MOVES.map((x) => x[1]));
  const extra = (c.actions || []).filter((l) => !opts.includes(l) && !seen.has(actionOf(l))).map((l) => [l, actionOf(l), actionOf(l) === "new date" ? "date" : undefined]);
  return (
    <div className="qmore">
      <button className="btn ghost tiny" aria-expanded={open} data-testid={`card-more-${c.id}`} onClick={() => setOpen(!open)}>More {open ? "▴" : "▾"}</button>
      {open && <div className="qmore-pop" data-testid={`card-more-pop-${c.id}`}><MoveRow title="" items={[...OLD_MOVES, ...extra]} c={c} ctx={ctx} testid={`card-more-moves-${c.id}`} /></div>}
    </div>
  );
}

function QuestionCard({ c, press, sel, onOpen, ctx }) {
  const [st, words] = effState(c, press);
  const [more, setMore] = useState(false);
  const cls = (STATE[st] || STATE.fine)[0];
  const opts = (c.options || []).slice(0, 3);
  const thens = c.then || [];
  const rows = [["What and why", c.what], ["Where it stands", c.stands], ["Next", c.next], ["Impact", c.impact]].filter((r) => r[1]);
  return (
    <article className={`vcard qcard ${cls}${sel ? " sel" : ""}`} data-testid={`card-${c.id}`} data-opens-card role="link" tabIndex={0}
      aria-label={c.title} onClick={() => onOpen(c)} onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) onOpen(c); }}>
      {st === "pressed" && <StateLine state={st} words={words} testid={`card-state-${c.id}`} />}
      <div className="qhead" data-testid={`card-title-${c.id}`}>{c.title}</div>
      <h3 className="q" data-testid={`card-question-${c.id}`}>{c.question}</h3>
      {c.context ? <p className="qctx" data-testid={`card-context-${c.id}`}>{c.context}</p> : null}
      <div className="qdue" data-testid={`card-due-${c.id}`}>
        {c.due && <span>Due {longDay(c.due)}</span>}
        {c.late === true && <span className="latechip" data-testid={`card-late-${c.id}`}>LATE</span>}
        <Disc name={c.owner} />
      </div>
      <div className="qopts" onClick={stop} onKeyDown={stop} data-testid={`card-options-${c.id}`}>
        {opts.map((o, i) => (
          <div className="qopt" key={o}>
            <button className={`btn${i === 0 ? " primary" : ""}`} disabled={!(ctx?.canAct && ctx.canPress(c, o))} data-testid={`card-option-${c.id}-${i}`}
              onClick={() => ctx.act(panelOf(c), o, c.id, "", c.topic || "")}>{o}</button>
            {thens[i] ? <span className="qthen" data-testid={`card-then-${c.id}-${i}`}>{thens[i]}</span> : null}
          </div>
        ))}
      </div>
      {c.default && <div className="qdef" data-testid={`card-default-${c.id}`}>If no answer by {longDay(c.default_by)}: {c.default}</div>}
      <div className="qfoot" onClick={stop} onKeyDown={stop}>
        {ctx && <MoreMenu c={c} ctx={ctx} />}
        <button className="btn ghost tiny" aria-expanded={more} data-testid={`card-readmore-${c.id}`} onClick={() => setMore(!more)}>{more ? "Read less" : "Read more"}</button>
      </div>
      {more && (
        <div className="qmore-rows" data-testid={`card-readmore-rows-${c.id}`}>
          {rows.map(([k, v]) => <p key={k}><span className="label">{k}</span> {v}</p>)}
        </div>
      )}
    </article>
  );
}

export function BoardCard({ c, press, sel, onOpen, ctx }) {
  if (c.question) return <QuestionCard c={c} press={press} sel={sel} onOpen={onOpen} ctx={ctx} />;
  const [st, words] = effState(c, press);
  const cls = (STATE[st] || STATE.fine)[0];
  const meta = [c.due, c.age].filter(Boolean);
  if (c.team_only) {
    return (
      <article className={`vcard ${cls}${sel ? " sel" : ""}`} data-testid={`card-${c.id}`} data-opens-card role="link" tabIndex={0}
        onClick={() => onOpen(c)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(c); }}>
        <StateLine state={st} words={words} testid={`card-state-${c.id}`} />
        <div className="head"><h3 className="title">{c.title}</h3></div>
        {c.due && <div className="meta" data-testid={`card-meta-${c.id}`}><span>{c.due}</span></div>}
      </article>
    );
  }
  return (
    <article className={`vcard ${cls}${sel ? " sel" : ""}`} data-testid={`card-${c.id}`} data-opens-card role="link" tabIndex={0}
      onClick={() => onOpen(c)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(c); }}>
      <StateLine state={st} words={words} testid={`card-state-${c.id}`} />
      <div className="head">
        <h3 className="title">{c.title}</h3>
        <Disc name={c.owner} />
        <button className="dots" aria-label="Mark this card" data-testid={`card-dots-${c.id}`} onClick={(e) => e.stopPropagation()}>⋯</button>
      </div>
      {c.context ? <p className="ctx">{c.context}</p> : null}
      <GroupBar g={c.group} />
      {c.next ? <div className="next"><b>Next:</b> {c.next}</div> : null}
      {(meta.length > 0 || c.impact) && (
        <div className="meta" data-testid={`card-meta-${c.id}`}>
          {c.due && <span>{c.due}</span>}
          {c.age && <span className={st === "late" ? "late-t" : ""}>{c.age}</span>}
          {c.impact && <span className="imp">Impact: <b>{c.impact}</b></span>}
        </div>
      )}
    </article>
  );
}

const DOT = { late: "var(--critical)", waiting: "var(--warn)", locked: "var(--accent)", stale: "var(--warn)", mistake: "var(--serious)", closed: "var(--good)" };

export function RowS({ c, right, chip, onOpen, testid, press }) {
  return (
    <button className={`rowS${chip ? " chiprow" : ""}${press ? " pressed" : ""}`} data-testid={testid} data-opens-card onClick={() => onOpen(c)}>
      <span className="dot" style={{ background: DOT[c.state] || "var(--ink-3)" }} />
      <span className="rt">{c.title} <span className="faint">· {c.id}</span></span>
      {chip ? <span className="chip tchip">{chip}</span> : (
        <>
          <span className="muted">{c.owner || ""}</span>
          <span className="faint">{right}</span>
        </>
      )}
    </button>
  );
}
